import React from 'react'
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import { useTheme } from '../../hooks/useTheme'

interface TurnstileCaptchaProps {
  onVerify: (token: string) => void
  onError?: () => void
  onExpire?: () => void
  size?: 'normal' | 'compact' | 'invisible'
  className?: string
  allowFallback?: boolean // Se true, permite continuar sem CAPTCHA em caso de erro
}

export interface TurnstileCaptchaHandle {
  reset: () => void
}

/**
 * Cloudflare Turnstile CAPTCHA Component
 *
 * Proteção anti-bot invisível para formulários
 * - 100% gratuito (sem limites)
 * - Invisível para usuários legítimos
 * - Melhor privacidade que reCAPTCHA
 *
 * Configuração:
 * 1. Obter keys em: https://dash.cloudflare.com/turnstile
 * 2. Adicionar domínio no dashboard: https://dash.cloudflare.com/turnstile
 * 3. Adicionar VITE_TURNSTILE_SITE_KEY no .env
 * 4. Adicionar TURNSTILE_SECRET_KEY no Supabase secrets
 *
 * Error Codes:
 * - 106010: Domain not allowed (adicionar domínio no dashboard)
 * - 110200: Invalid widget configuration
 * - 300XXX: Network/connectivity errors
 */
export const TurnstileCaptcha = React.forwardRef<TurnstileCaptchaHandle, TurnstileCaptchaProps>(({
  onVerify,
  onError,
  onExpire,
  size = 'invisible',
  className = '',
  allowFallback = false
}, ref) => {
  const { isDark } = useTheme()
  const turnstileRef = React.useRef<TurnstileInstance>(null)
  const [hasFailed, setHasFailed] = React.useState(false)
  const [errorCode, setErrorCode] = React.useState<string | null>(null)
  const hasCalledOnVerify = React.useRef(false) // Evitar múltiplas chamadas

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

  // 🔧 DESENVOLVIMENTO: Desativar CAPTCHA completamente em localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const isDevelopment = process.env.NODE_ENV === 'development' || isLocalhost
  const isUsingFallbackBypass = !isDevelopment && allowFallback && (!siteKey || siteKey === '0x4AAAAAAA...')

  // Expor método reset() para o parent re-emitir um token novo após erros
  // (por ex.: depois de um signUp/signIn falhar, o token foi consumido e a UI ficaria travada)
  React.useImperativeHandle(ref, () => ({
    reset: () => {
      hasCalledOnVerify.current = false
      setHasFailed(false)
      setErrorCode(null)
      if (isDevelopment) {
        hasCalledOnVerify.current = true
        onVerify('dev-bypass-token')
      } else if (isUsingFallbackBypass) {
        hasCalledOnVerify.current = true
        onVerify('fallback-bypass-token')
      } else {
        turnstileRef.current?.reset()
      }
    }
  }))

  if (isDevelopment) {
    React.useEffect(() => {
      // Garantir que só chama uma vez
      if (!hasCalledOnVerify.current) {
        hasCalledOnVerify.current = true
        onVerify('dev-bypass-token')
      }
    }, []) // Array de dependências vazio - roda só uma vez

    return null // Não renderiza nada em dev
  }

  // Validação de configuração (apenas em produção)
  if (!siteKey || siteKey === '0x4AAAAAAA...') {
    // Se permite fallback, usar token de bypass
    if (allowFallback) {
      React.useEffect(() => {
        if (!hasCalledOnVerify.current) {
          hasCalledOnVerify.current = true
          onVerify('fallback-bypass-token')
        }
      }, []) // Array vazio - roda só uma vez

      return null // Não renderiza nada em fallback
    }

    return (
      <div className="p-3 bg-blood-100 dark:bg-blood-500/20 dark:bg-blood-500/20 border border-blood-500 dark:border-blood-500 rounded-caderno-sm">
        <p className="text-sm text-blood-500 dark:text-blood-500">
          ⚠️ CAPTCHA não configurado. Configure VITE_TURNSTILE_SITE_KEY no .env
        </p>
      </div>
    )
  }

  const handleVerify = (token: string) => {
    if (!hasCalledOnVerify.current) {
      hasCalledOnVerify.current = true
      onVerify(token)
    }
  }

  const handleError = (errorCode?: string | Error) => {
    const code = errorCode?.toString() || 'unknown'
    setErrorCode(code)
    setHasFailed(true)

    // Mapear códigos de erro conhecidos
    const errorMessages: Record<string, string> = {
      '106010': 'Domain not allowed. Add your domain to Cloudflare Turnstile dashboard.',
      '110200': 'Invalid widget configuration. Check your site key.',
      '300110': 'Network error. Check your internet connection.',
      '300120': 'Network timeout. Please try again.',
      '300130': 'Network connection failed.',
      '300140': 'Network request blocked by browser or firewall.'
    }

    const message = errorMessages[code] || `Unknown error (${code})`

    // Se permitido, fazer fallback (bypass)
    if (allowFallback && (code === '106010' || code === '110200')) {
      onVerify('fallback-bypass-token')
      return
    }

    if (onError) {
      onError()
    }
  }

  const handleExpire = () => {
    hasCalledOnVerify.current = false // Reset para permitir nova verificação
    if (onExpire) {
      onExpire()
    }
  }

  // Se falhou e não permite fallback, mostrar mensagem de erro
  if (hasFailed && !allowFallback && errorCode) {
    const isConfigError = errorCode === '106010' || errorCode === '110200'

    return (
      <div className={className}>
        <div className={`p-3 border rounded-caderno-sm ${
          isConfigError
            ? 'bg-ochre-100 dark:bg-ochre-500/20 dark:bg-ochre-500/20 border-ochre-500 dark:border-ochre-500'
            : 'bg-blood-100 dark:bg-blood-500/20 dark:bg-blood-500/20 border-blood-500 dark:border-blood-500'
        }`}>
          <p className={`text-sm ${
            isConfigError
              ? 'text-ochre-500 dark:text-ochre-500'
              : 'text-blood-500 dark:text-blood-500'
          }`}>
            {errorCode === '106010' && (
              <>
                ⚠️ <strong>Domain not configured:</strong> Add this domain to your Cloudflare Turnstile widget.
                <br />
                <a
                  href="https://dash.cloudflare.com/turnstile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Configure in Cloudflare Dashboard →
                </a>
              </>
            )}
            {errorCode === '110200' && '⚠️ CAPTCHA configuration error. Please contact support.'}
            {!['106010', '110200'].includes(errorCode) && `⚠️ CAPTCHA error (${errorCode}). Please try again.`}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <Turnstile
        ref={turnstileRef}
        siteKey={siteKey}
        onSuccess={handleVerify}
        onError={handleError}
        onExpire={handleExpire}
        options={{
          theme: isDark ? 'dark' : 'light',
          size,
          language: 'pt-br', // IMPORTANTE: Cloudflare aceita apenas lowercase (pt-br, não pt-BR)
          // Configurações de segurança
          responseFieldName: 'cf-turnstile-response',
          retry: 'auto',
          retryInterval: 8000,
          execution: 'render', // Executa ao renderizar
          refreshExpired: 'auto', // Auto-refresh quando expirar
          appearance: 'interaction-only' // Só aparecer quando necessário
        }}
      />
    </div>
  )
})

TurnstileCaptcha.displayName = 'TurnstileCaptcha'

export default TurnstileCaptcha
